import React, { useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";

/**
 * Address input using Google Maps Places API (google.maps.places.Autocomplete).
 * Replaces react-google-places-autocomplete with native Places API.
 * See: https://developers.google.com/maps/documentation/javascript/place-autocomplete
 */
const PlacesAddressInput = ({
  apiKey,
  value = null,
  onChange,
  placeholder = "Sélectionner une adresse",
  options = {}
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!apiKey || !inputRef.current) return;

    const loader = new Loader({
      apiKey,
      version: "weekly",
      libraries: ["places"]
    });

    loader
      .load()
      .then(() => {
        if (!inputRef.current || !window.google?.maps?.places) return;
        const Autocomplete = window.google.maps.places.Autocomplete;
        autocompleteRef.current = new Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: "ca" },
          fields: ["formatted_address", "place_id", "address_components"],
          ...options
        });
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();
          const label = place?.formatted_address || "";
          if (label && onChange) {
            onChange({
              label,
              value: {
                description: label,
                place_id: place.place_id,
                structured_formatting: { main_text: label }
              }
            });
          }
        });
        setLoaded(true);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load Google Maps");
      });

    return () => {
      autocompleteRef.current = null;
    };
  }, [apiKey, onChange, options]);

  if (error) {
    return (
      <input
        type="text"
        placeholder={placeholder}
        defaultValue={value?.label}
        readOnly
        style={{
          width: "100%",
          padding: "4px 8px",
          fontSize: "16px",
          border: "2px solid #ff4d4f",
          borderRadius: "5px",
          backgroundColor: "#fff2f0"
        }}
        title={error}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder}
      defaultValue={value?.label ?? ""}
      style={{
        width: "100%",
        padding: "4px 8px",
        fontSize: "16px",
        border: "2px solid #ddd",
        borderRadius: "5px",
        boxSizing: "border-box",
        outline: "none",
        transition: "border-color 0.3s"
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#ff114d";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#ddd";
      }}
    />
  );
};

export default PlacesAddressInput;
